import { useContext } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationConfirmPasscode } from '@cash-web/shared-data-access-api/react-query/useMutationConfirmPasscode'
// eslint-disable-next-line @nx/workspace-no-new-franklin-get-profile -- pre-existing usage; migrate per #cash-profile-migration
import { useQueryGetProfile } from '@cash-web/shared-data-access-api/react-query/useQueryGetProfile'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { useResetPasscode } from '../../../hooks/useResetPasscode'
import { getVerifyPasscodeErrors } from '../../../util/statusErrors'
import { BaseBlockerFormFields } from '../..'
import { BlockerPasscodeEntry } from '../BlockerPasscodeCreation/BlockerPasscodeEntry'

type FormFields = BaseBlockerFormFields & {
  passcode: string
}

export function ConfirmPasscode() {
  const { t } = useTranslation()
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const { clearError } = useContext(ErrorContext)

  const { resetPasscodeHelpItem } = useResetPasscode()
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const flow = useFlowContext()

  const { handleSubmit, setValue } = form
  const { mutateAsync: confirmPasscode } = useMutationConfirmPasscode({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
    performOptimisticUpdate: setQueryData => {
      // Don't perform optimistic update if we're onboarding
      if (flow.active?.clientScenario === ClientScenario.ONBOARDING) {
        return
      }
      setQueryData(useQueryGetProfile, ({ queryData }) => {
        return {
          ...queryData,
          profile: {
            ...queryData.profile,
            require_passcode_confirmation: true,
          },
        }
      })
    },
  })

  const onSubmit = async ({ passcode }: FormFields) => {
    const transfer_token = flow.active?.transferToken ?? ''
    const payment_tokens = flow.active?.paymentTokens ?? undefined

    await handleBlockerResponse({
      promisedBlockerUpdate: confirmPasscode({
        passcode,
        transfer_token,
        payment_tokens,
      }),
      errors: getVerifyPasscodeErrors(genericError, t),
      onFailure: () => setValue('passcode', '', { shouldValidate: true }),
    })
  }

  return (
    <BlockerPasscodeEntry
      form={form}
      mainText={t('Enter your PIN', {
        context: 'Title text. PIN (Personal Identitification Number) is a numeric 4 digit code.',
      })}
      secondaryText={t('This helps keep your account secure.', { context: 'Subtitle text.' })}
      helpModal={{ helpItems: [resetPasscodeHelpItem] }}
      length={4}
      name="passcode"
      onSubmit={() => {
        clearError()
        handleSubmit(onSubmit)()
      }}
    />
  )
}
